import { Box } from '@apt-snap/ui'
import { useEnterExit } from '@thesoulfresh/react-tools'
import React from 'react'
import { ViewStyle } from 'react-native'
import { useIsFirstRender } from '~/hooks'
import { getEasing, getSpeed, useTheme } from '@apt-snap/ui'
import { mergeRefs, removeNulls } from '~/utils'
import type { StyleTransitionProps } from './types'
import { isTransformProperty } from './utils'
import { WritableProps } from 'tsdef'

const camelToKebab = (str: string): string => {
  return str.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`)
}

export const AnimatedStyleTransition = React.forwardRef(
  (
    {
      state: stateProp = true,
      animateEntrance = false,
      persistent = true,
      transitions = [],
      style,
      debug,
      children,
      ...rest
    }: StyleTransitionProps,
    ref,
  ) => {
    const theme = useTheme()
    const isFirstRender = useIsFirstRender()

    const [normalizedTransitions, longestTransition] = React.useMemo(() => {
      let longestTransition: number
      let longestTransitionIndex: number

      const _normalizedTransitions = transitions
        .map((curr, index) => {
          if (curr.property) {
            const isTransform = isTransformProperty(curr.property)
            // cssProperty is used for the transition string (kebab-case for CSS)
            const cssProperty = isTransform
              ? 'transform'
              : camelToKebab(curr.property)
            // styleProperty is used for setting the style object (camelCase for React)
            const styleProperty = isTransform ? 'transform' : curr.property

            const easing = getEasing(theme, curr.easing) || 'linear'
            const duration = getSpeed(theme, curr.duration ?? 'm')
            const outDuration = getSpeed(
              theme,
              curr.outDuration ?? curr.duration ?? 'm',
            )
            const delay = getSpeed(theme, curr.delay)

            const transitionLength = Math.max(
              (duration || 0) + (delay || 0),
              (outDuration || 0) + (delay || 0),
            )
            if (!longestTransition) {
              // eslint-disable-next-line react-hooks/immutability
              longestTransition = transitionLength
              longestTransitionIndex = 0
            } else {
              if (longestTransition < transitionLength) {
                longestTransition = transitionLength
                longestTransitionIndex = index
              }
            }

            return {
              ...curr,
              isTransform,
              cssProperty,
              styleProperty,
              easing,
              duration: duration != null ? `${duration}ms` : undefined,
              outDuration: outDuration != null ? `${outDuration}ms` : undefined,
              delay: delay != null ? `${delay}ms` : undefined,
            }
          } else {
            return undefined
          }
        })
        .filter(removeNulls)

      return [
        _normalizedTransitions,
        _normalizedTransitions[longestTransitionIndex],
      ]
    }, [transitions, theme])

    const { ref: animRef, state: entranceState } = useEnterExit(
      stateProp,
      longestTransition?.cssProperty,
      // Give the DOM a little time to settle before we start the animation.
      // This seems to fix an issue where in some cases animations advance
      // directly to the next state without animating. I think it has something
      // to do with React trying to batch updates to the DOM.
      { enterDelay: animateEntrance ? 60 : undefined },
    )

    let progress = entranceState === 'entered' ? 1 : 0
    if (!animateEntrance && isFirstRender && entranceState !== 'exited') {
      progress = 1 - progress
    }

    const previousState = React.useRef(
      animateEntrance
        ? entranceState === 'exited' || entranceState === 'entering'
          ? 'exited'
          : 'entered'
        : // If we are not animating the initial entrance, then default to the
          // state that we will enter on.
          entranceState === 'exited' || entranceState === 'entering'
          ? 'entered'
          : 'exited',
    )

    if (debug) {
      // prettier-ignore
      console.log(
        `[StyleTransition] RENDER -> "${progress}"`,
        '\n  state:', stateProp,
        '\n  animateEntrance:', animateEntrance,
        '\n  entranceState:', entranceState,
        '\n  previousState:', previousState.current,
      )
    }

    // Listen to transitionend events to call onComplete after CSS animations finish
    React.useEffect(() => {
      const element = animRef.current
      if (!element) return

      const handleTransitionEnd = (event: TransitionEvent) => {
        // Only respond to transitions on the element itself, not bubbled events
        if (event.target !== element) return

        // Check if this is the longest transition completing
        if (
          longestTransition &&
          event.propertyName === longestTransition.cssProperty
        ) {
          const currentProgress = entranceState === 'entered' ? 1 : 0

          if (debug) {
            console.log(
              `[StyleTransition] transitionend -> "${currentProgress}"`,
              '\n  propertyName:',
              event.propertyName,
              '\n  entranceState:',
              entranceState,
            )
          }

          normalizedTransitions.forEach(({ onComplete }) => {
            onComplete?.(currentProgress, true)
          })
        }
      }

      element.addEventListener('transitionend', handleTransitionEnd)
      return () => {
        element.removeEventListener('transitionend', handleTransitionEnd)
      }
    }, [
      animRef,
      normalizedTransitions,
      longestTransition,
      entranceState,
      debug,
    ])

    if (entranceState === 'exited' && !persistent) {
      return null
    }

    type TransformType = ViewStyle['transform']

    // TODO Allow tranforms of different durations:
    // CSS doesn't provide a mechanism for setting different transitions for
    // each transform so you can't do a scale for 500ms with a translate of
    // 800ms. Instead you need to create multiple elements that each get
    // individual translations. We'd need to automate that here if we want to
    // match the functionality from the native side which does allow separate
    // transition durations per translation definition.
    const a = normalizedTransitions.reduce(
      (acc, curr) => {
        // Collect the transitions into an array now, concatenate them later
        if (!acc.transition) acc.transition = []
        // Remove duplicate transition definitions
        else {
          acc.transition = acc.transition.filter(
            (t) => !t.startsWith(curr.cssProperty + ' '),
          )
        }

        // Use outDuration when exiting (progress = 0) and duration when entering (progress = 1)
        const isExiting = progress === 0
        const activeDuration = isExiting ? curr.outDuration : curr.duration

        acc.transition.push(
          [curr.cssProperty, activeDuration, curr.easing, curr.delay]
            .filter((t) => !!t)
            .join(' '),
        )

        const value =
          typeof curr.value === 'function'
            ? curr.value(progress) || 0
            : progress

        type NonStringTransform = WritableProps<Exclude<TransformType, string>>
        function isNonStringTransform(
          transform: TransformType,
        ): transform is NonStringTransform {
          return typeof transform !== 'string'
        }

        if (curr.isTransform) {
          if (!acc.transform) acc.transform = []
          if (isNonStringTransform(acc.transform)) {
            acc.transform.push({
              [curr.property]: value,
            } as any)
          } else {
            // TODO What to do here?
            console.error(
              'Unable to append transition to an existing string transform',
            )
          }
        } else {
          acc[curr.styleProperty] = value
        }

        return acc
      },
      {} as { transition: string[]; transform: TransformType },
    )

    if (debug) {
      console.log('[StyleTransition] animConfig', JSON.stringify(a, null, 2))
    }

    const animStyle = {
      ...a,
      transition: a.transition?.join(', '),
    }

    return (
      <Box
        testID="StyleTransition"
        style={[style, animStyle]}
        ref={mergeRefs([ref, animRef])}
        children={children}
        {...rest}
      />
    )
  },
)

/**
 * An implementation of StyleTransition that does not animate (useful for
 * testing)
 */
export const StaticStyleTransition = React.forwardRef(
  (props: StyleTransitionProps, ref) => <Box {...props} ref={ref} />,
)

/**
 * `<StyleTransition>` allows you to transition animatable style properties
 * between two states. It also allows animating the entrance/exit of a
 * component.
 *
 * Use the `progress` value to set the current state (from 0 - 1) of the animated props.
 * By default, the component will auto transition into the first state (ie. from
 * progress 0 -> 1). However, you can disable entrance animations by passing
 * `animateEntrance={false}`.
 */
export const StyleTransition = React.forwardRef(
  ({ disableAnimations, ...rest }: StyleTransitionProps, ref) => {
    return disableAnimations ? (
      <StaticStyleTransition {...rest} ref={ref} />
    ) : (
      <AnimatedStyleTransition {...rest} ref={ref} />
    )
  },
)
